import React from 'react';
import { Button, Input } from 'antd';
import {
    asyncCreateUserScore,
    asyncFetchUserScores,
} from 'store/reducers/users/thunks';
import ScoreHistory from 'layouts/scoreHistory/ScoreHistory';
import {
    IKeyedUserScore,
    INewUserScore,
    IScoreType,
} from 'store/reducers/users/types';
import { useAppDispatch, useAppSelector } from 'store/store';
import ScoreTypeChip from 'components/blocks/scoreTypeChip/ScoreTypeChip';

interface SaveButtonProps {
    userId: number;
    newUserScores: INewUserScore[];
    setNewUserScores: React.Dispatch<React.SetStateAction<INewUserScore[]>>;
}

function SaveButton({
    userId,
    newUserScores,
    setNewUserScores,
}: SaveButtonProps): React.ReactElement {
    const dispatch = useAppDispatch();
    return (
        <Button
            onClick={() => {
                dispatch(
                    asyncCreateUserScore({
                        userId,
                        userScores: newUserScores,
                    })
                )
                    .unwrap()
                    .then(() => {
                        dispatch(asyncFetchUserScores(userId));
                        setNewUserScores([]);
                    });
            }}
            style={{ color: 'green' }}
        >
            Сохранить
        </Button>
    );
}

interface AddButtonProps {
    setNewUserScores: React.Dispatch<React.SetStateAction<INewUserScore[]>>;
    userScoreForm: INewUserScore;
    setUserScoreForm: React.Dispatch<React.SetStateAction<INewUserScore>>;
}

function AddButton({
    setNewUserScores,
    userScoreForm,
    setUserScoreForm,
}: AddButtonProps): React.ReactElement {
    return (
        <Button
            onClick={() => {
                setNewUserScores(state => [
                    ...state,
                    { ...userScoreForm, id: state.length + 1 },
                ]);
                setUserScoreForm({
                    id: 1,
                    count: 0,
                    comment: '',
                    date: userScoreForm.date,
                    type_id: 1,
                });
            }}
        >
            Добавить
        </Button>
    );
}

interface ScoreTypeChipsProps {
    scoreTypes: IScoreType[];
    setUserScoreForm: React.Dispatch<React.SetStateAction<INewUserScore>>;
    userScoreForm: INewUserScore;
}

function ScoreTypeChips({
    scoreTypes,
    setUserScoreForm,
    userScoreForm,
}: ScoreTypeChipsProps): React.ReactElement {
    return (
        <div className="score-types-chips-flexbox">
            {scoreTypes.map(scoreType => (
                <ScoreTypeChip
                    key={scoreType.id}
                    onClick={() =>
                        setUserScoreForm({
                            ...userScoreForm,
                            type_id: scoreType.id,
                            count:
                                userScoreForm.type_id === scoreType.id
                                    ? userScoreForm.count + scoreType.count
                                    : scoreType.count,
                            comment: scoreType.name,
                        })
                    }
                >
                    {scoreType.name}
                </ScoreTypeChip>
            ))}
        </div>
    );
}

function UserScoreForm({
    userId,
    date,
}: {
    userId: number;
    date: Date;
}): React.ReactElement {
    const [newUserScores, setNewUserScores] = React.useState<INewUserScore[]>(
        []
    );

    const [userScoreForm, setUserScoreForm] = React.useState<INewUserScore>({
        id: 1,
        count: 0,
        comment: '',
        date,
        type_id: 1,
    });

    React.useEffect(() => {
        setUserScoreForm(state => ({ ...state, date }));
    }, [date]);

    const { scoreTypes } = useAppSelector(state => state.usersStore);

    const [formIsEmpty, setFormIsEmpty] = React.useState(false);

    React.useEffect(() => {
        setFormIsEmpty(!(userScoreForm.count && userScoreForm.comment));
    }, [userScoreForm.comment, userScoreForm.count]);

    return (
        <>
            <ScoreTypeChips
                userScoreForm={userScoreForm}
                setUserScoreForm={setUserScoreForm}
                scoreTypes={scoreTypes}
            />

            <Input
                placeholder="Количество баллов"
                onChange={e =>
                    setUserScoreForm({
                        ...userScoreForm,
                        count: Number(e.target.value),
                    })
                }
                value={userScoreForm.count}
            />

            <Input
                placeholder="Комментарий"
                onChange={e =>
                    setUserScoreForm({
                        ...userScoreForm,
                        comment: e.target.value,
                    })
                }
                value={userScoreForm.comment}
            />

            {!formIsEmpty && (
                <AddButton
                    setUserScoreForm={setUserScoreForm}
                    setNewUserScores={setNewUserScores}
                    userScoreForm={userScoreForm}
                />
            )}

            <ScoreHistory userScores={newUserScores as IKeyedUserScore[]} />

            {newUserScores.length > 0 && (
                <SaveButton
                    setNewUserScores={setNewUserScores}
                    newUserScores={newUserScores}
                    userId={userId}
                />
            )}
        </>
    );
}

export default React.memo(UserScoreForm);
